import styled, { keyframes } from "styled-components";
import { usePage } from "@inertiajs/react";
import { useLandingLanguage } from "../../../contexts/landing-language";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BookingSection = styled.section`
  background: #faf8f5;
  padding: 100px 24px;
  position: relative;
  z-index: 10;
`;

const BookingContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 4px;
  padding: 50px 60px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.8s ease forwards;

  @media (max-width: 768px) {
    padding: 32px 24px;
    margin-top: -40px;
  }
`;

const BookingTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: #c9a86c;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const BookingSubtitle = styled.p`
  font-size: 32px;
  font-weight: 300;
  color: #1a1a1a;
  margin-bottom: 40px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const OtaList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const OtaLink = styled.a`
  border: 1px solid #e5e1dc;
  border-radius: 8px;
  padding: 18px;
  text-decoration: none;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
    border-color: #c9a86c;
  }
`;

const OtaLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 6px;
  background: #fff;
`;

const OtaMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OtaName = styled.span`
  font-weight: 600;
`;

const OtaText = styled.span`
  color: #8b7355;
  font-size: 13px;
`;

const QuickInfo = styled.div`
  margin-top: 50px;
  padding-top: 50px;
  border-top: 1px solid #e5e1dc;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  text-align: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const InfoItem = styled.div`
  h4 {
    font-size: 42px;
    font-weight: 300;
    color: #1a1a1a;
    margin-bottom: 8px;

    span {
      color: #c9a86c;
    }
  }

  p {
    font-size: 13px;
    color: #8b7355;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
`;

const BookingWidget = () => {
  const { registered_otas } = usePage().props;
  const { t } = useLandingLanguage();
  const b = t.booking;

  return (
    <BookingSection id="booking">
      <BookingContainer>
        <BookingTitle>{b.subtitle}</BookingTitle>
        <BookingSubtitle>{b.description}</BookingSubtitle>

        {/* Legacy booking form is intentionally disabled for now.
            Keep this section for future in-site booking feature rollout. */}

        <OtaList>
          {(registered_otas || []).map((ota) => (
            <OtaLink
              key={ota.name}
              href={ota.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ota.logo_url && (
                <OtaLogo src={ota.logo_url} alt={`${ota.name} logo`} />
              )}
              <OtaMeta>
                <OtaName>{ota.name}</OtaName>
                <OtaText>{ota.short_desc}</OtaText>
              </OtaMeta>
            </OtaLink>
          ))}
        </OtaList>

        <QuickInfo>
          <InfoItem>
            <h4>
              50<span>+</span>
            </h4>
            <p>{b.stats.roomsLabel}</p>
          </InfoItem>
          <InfoItem>
            <h4>
              4.8<span>/5</span>
            </h4>
            <p>{b.stats.ratingLabel}</p>
          </InfoItem>
          <InfoItem>
            <h4>
              24<span>/7</span>
            </h4>
            <p>{b.stats.supportLabel}</p>
          </InfoItem>
        </QuickInfo>
      </BookingContainer>
    </BookingSection>
  );
};

export default BookingWidget;
